import './DateField.pcss';

export type DateFieldProps = {
    label: string;
    id: string;
    value?: string;
    placeholder?: string;
    focusPlaceholder?: string;
    activePlaceholder?: string;
    active?: boolean;
};

export default class DateField extends HTMLElement {
    readonly #input = this.querySelector('[data-role="date-field-input"]') as HTMLInputElement;

    get input(): HTMLInputElement {
        return this.#input;
    }

    readonly #clearButton = this.querySelector('[data-role="clear"]') as HTMLInputElement;

    #props: Partial<DateFieldProps> = {};

    public connectedCallback(): void {
        this.#props = {
            placeholder: this.#input.placeholder,
            focusPlaceholder: this.#input.dataset.focusPlaceholder,
            activePlaceholder: this.#input.dataset.activePlaceholder
        };

        this.#handleInputChange();

        this.#input.addEventListener('change', this.#handleInputChange);
        this.#input.addEventListener('input', this.#handleInputChange);

        this.#clearButton.addEventListener('click', this.#handleClearButtonClick);

        this.addEventListener('click', this.setInputFocus);
        this.#input.addEventListener('focus', this.#handleInputFocusIn);
        this.#input.addEventListener('focusin', this.#handleInputFocusIn);
        this.#input.addEventListener('focusout', this.#handleInputFocusOut);
    }

    public disconnectedCallback(): void {
        this.#input.removeEventListener('change', this.#handleInputChange);
        this.#input.removeEventListener('input', this.#handleInputChange);

        this.#clearButton.removeEventListener('click', this.#handleClearButtonClick);

        this.removeEventListener('click', this.setInputFocus);
        this.#input.removeEventListener('focus', this.#handleInputFocusIn);
        this.#input.removeEventListener('focusin', this.#handleInputFocusIn);
        this.#input.removeEventListener('focusout', this.#handleInputFocusOut);
    }

    readonly #handleInputChange = (): void => {
        const hasInputValue = this.#input.value.length > 0;
        this.#toggleClearButtonHiddenAttribute(!hasInputValue);
        this.#toggleEmptyAttribute(!hasInputValue);
    };

    readonly setActive = (): void => {
        this.toggleAttribute('active', true);

        if (!this.#props.activePlaceholder) {
            return;
        }

        this.#input.placeholder = this.#props.activePlaceholder;
    };

    readonly setInactive = (): void => {
        this.removeAttribute('active');

        if (!this.#props.placeholder) {
            return;
        }

        this.#input.placeholder = this.#props.placeholder;
    };

    readonly #handleClearButtonClick = (): void => {
        this.#input.value = '';
        this.#input.dispatchEvent(new Event('change'));
        this.#toggleClearButtonHiddenAttribute(true);
        this.dispatchEvent(new Event('clear'));
    };

    readonly #toggleEmptyAttribute = (show: boolean): void => {
        this.toggleAttribute('empty', show);
    };

    readonly #toggleClearButtonHiddenAttribute = (force: boolean): void => {
        this.#clearButton.toggleAttribute('hidden', force);
    };

    /** @deprecated */
    readonly setInputFocus = (): void => {
        this.#input.focus({ preventScroll: true });
    };

    /** @deprecated */
    readonly #handleInputFocusIn = (): void => {
        if (!this.#props.focusPlaceholder) {
            return;
        }

        this.#input.placeholder = this.#props.focusPlaceholder;
    };

    /** @deprecated */
    readonly #handleInputFocusOut = (): void => {
        this.removeAttribute('active');

        if (!this.#props.placeholder) {
            return;
        }

        this.#input.placeholder = this.#props.placeholder;
    };
}

if (!customElements.get('nh-date-field')) {
    customElements.define('nh-date-field', DateField);
}
